import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Plus, Trash2, Loader2, ChevronDown, ChevronUp, AlertTriangle, Users, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import {
  PSYCH_REFERRAL_OPTIONS, isPsychiatrist, isCapacityFresh, daysUntilExpiry, SEVEN_DAYS_MS,
} from "../lib/capacity";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Detect "the SQL migration hasn't been applied yet" so we degrade gracefully.
const isMissingSchemaError = (error) => {
  if (!error) return false;
  const code = String(error.code || "");
  const msg = String(error.message || "").toLowerCase();
  return ["42703", "42P01", "PGRST204", "PGRST205"].includes(code)
    || msg.includes("does not exist")
    || msg.includes("schema cache")
    || msg.includes("could not find");
};

const MIGRATION_MSG = "Precise capacity isn’t switched on for your account yet. It will start working as soon as the capacity update is applied.";

export const CapacityEditor = ({ clinician, onUpdated, locked = false, demoMode = false, dates = [] }) => {
  const psych = isPsychiatrist(clinician);
  const [expanded, setExpanded] = useState(false);
  const [count, setCount] = useState("");
  const [timing, setTiming] = useState("now");
  const [fromDate, setFromDate] = useState(todayISO());
  const [stagger, setStagger] = useState([{ patient_count: "1", available_from: todayISO() }]);
  const [referralType, setReferralType] = useState("");
  const [referralOther, setReferralOther] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const hasSaved = clinician?.capacity_count != null || Boolean(clinician?.capacity_timing_type);
  const fresh = isCapacityFresh(clinician);
  const daysLeft = daysUntilExpiry(clinician);
  const expired = hasSaved && !fresh;

  useEffect(() => {
    if (!clinician) return;
    setCount(clinician.capacity_count != null ? String(clinician.capacity_count) : "");
    setTiming(clinician.capacity_timing_type || "now");
    setFromDate(clinician.capacity_from_date || todayISO());
    setReferralType(clinician.psychiatry_referral_type || "");
    setReferralOther(clinician.psychiatry_referral_type_other || "");
    setExpanded(Boolean(clinician.capacity_count != null || clinician.capacity_timing_type || clinician.psychiatry_referral_type));
    if (demoMode) {
      if (dates && dates.length) {
        setStagger(dates.map((d) => ({ patient_count: String(d.patient_count), available_from: d.available_from })));
      }
      return;
    }
    (async () => {
      try {
        const { data, error } = await supabase
          .from("clinician_capacity_dates")
          .select("patient_count,available_from")
          .eq("clinician_id", clinician.id)
          .order("available_from", { ascending: true });
        if (!error && data && data.length) {
          setStagger(data.map((d) => ({ patient_count: String(d.patient_count), available_from: d.available_from })));
        }
      } catch { /* table may not exist yet — safe to ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinician?.id]);

  const updateStaggerRow = (index, patch) =>
    setStagger((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const addStaggerRow = () =>
    setStagger((rows) => [...rows, { patient_count: "1", available_from: todayISO() }]);
  const removeStaggerRow = (index) =>
    setStagger((rows) => (rows.length > 1 ? rows.filter((_, i) => i !== index) : rows));

  const save = async () => {
    if (locked || saving) return;
    const now = new Date();
    const patch = {
      capacity_last_confirmed_at: now.toISOString(),
      capacity_expires_at: new Date(now.getTime() + SEVEN_DAYS_MS).toISOString(),
    };
    if (psych) patch.psychiatry_referral_type = referralType || null;
    if (psych) patch.psychiatry_referral_type_other = referralType === "other" ? (referralOther.trim() || null) : null;

    const parsedCount = parseInt(count, 10);
    const usingStagger = timing === "staggered";
    const usingCount = !usingStagger && Number.isFinite(parsedCount) && parsedCount > 0;
    let staggerRows = [];

    if (usingStagger) {
      staggerRows = stagger
        .map((row) => ({ patient_count: parseInt(row.patient_count, 10), available_from: row.available_from }))
        .filter((row) => Number.isFinite(row.patient_count) && row.patient_count > 0 && row.available_from);
      if (!staggerRows.length) return toast.error("Add at least one dated opening.");
      patch.capacity_timing_type = "staggered";
      patch.capacity_count = null;
      patch.capacity_from_date = null;
    } else if (usingCount) {
      patch.capacity_count = parsedCount;
      patch.capacity_timing_type = timing;
      patch.capacity_from_date = timing === "from_date" ? fromDate : null;
    } else if (psych && referralType) {
      patch.capacity_count = null;
      patch.capacity_timing_type = null;
      patch.capacity_from_date = null;
    } else {
      return toast.error(`Enter how many patients you can take, choose staggered dates${psych ? ", or set your referral type." : "."}`);
    }

    if (demoMode) {
      onUpdated?.({ ...clinician, ...patch, _demoStaggerRows: usingStagger ? staggerRows : [] });
      toast.success("Demo updated — changes are not saved.");
      return;
    }

    setSaving(true);
    try {
      if (usingStagger) {
        await supabase.from("clinician_capacity_dates").delete().eq("clinician_id", clinician.id);
        const { error: insertError } = await supabase
          .from("clinician_capacity_dates")
          .insert(staggerRows.map((row) => ({ ...row, clinician_id: clinician.id })));
        if (insertError) throw insertError;
      } else {
        try { await supabase.from("clinician_capacity_dates").delete().eq("clinician_id", clinician.id); } catch { /* ignore */ }
      }
      const { data, error } = await supabase.from("clinicians").update(patch).eq("id", clinician.id).select().maybeSingle();
      if (error) throw error;
      if (data) onUpdated?.(data);
      toast.success("Capacity confirmed — GPs will see it for the next 7 days.");
    } catch (error) {
      toast.error(isMissingSchemaError(error) ? MIGRATION_MSG : "Could not save your capacity.");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (clearing) return;
    if (demoMode) {
      onUpdated?.({
        ...clinician,
        capacity_count: null, capacity_timing_type: null, capacity_from_date: null,
        capacity_last_confirmed_at: null, capacity_expires_at: null, _demoStaggerRows: [],
      });
      setCount(""); setTiming("now"); setStagger([{ patient_count: "1", available_from: todayISO() }]);
      toast.success("Demo capacity cleared — changes are not saved.");
      return;
    }
    setClearing(true);
    try { await supabase.from("clinician_capacity_dates").delete().eq("clinician_id", clinician.id); } catch { /* ignore */ }
    try {
      const { data, error } = await supabase.from("clinicians").update({
        capacity_count: null, capacity_timing_type: null, capacity_from_date: null,
        capacity_last_confirmed_at: null, capacity_expires_at: null,
      }).eq("id", clinician.id).select().maybeSingle();
      if (error && !isMissingSchemaError(error)) throw error;
      if (data) onUpdated?.(data);
      setCount(""); setTiming("now"); setStagger([{ patient_count: "1", available_from: todayISO() }]);
      toast.success("Precise numbers removed — GPs will see your general availability only.");
    } catch {
      toast.error("Could not remove precise capacity.");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="mt-5 rounded-md border border-slate-200 bg-slate-50" data-testid="capacity-editor">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        disabled={locked}
        className="flex w-full items-center justify-between gap-2 p-4 text-left disabled:cursor-not-allowed disabled:opacity-60"
        data-testid="capacity-toggle"
      >
        <span className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <span className="font-medium text-slate-900">Want to be more specific?</span>
          <span className="hidden text-xs text-slate-500 sm:inline">Optional — tell GPs exactly how many patients you can take</span>
        </span>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="space-y-4 border-t border-slate-200 p-4">
          {fresh && (
            <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="capacity-fresh-notice">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              GPs can see your exact capacity. It stays visible for {daysLeft > 0 ? `${daysLeft} more day${daysLeft === 1 ? "" : "s"}` : "a few more hours"} — reconfirm below to keep it fresh.
            </div>
          )}
          {expired && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="capacity-expired-notice">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Your exact numbers are more than 7 days old, so they’re hidden from GPs. Reconfirm below to show them again (your general availability is unaffected).
            </div>
          )}

          <div className="space-y-2">
            <Label>When can you take new patients?</Label>
            <RadioGroup value={timing} onValueChange={setTiming} className="grid gap-2 sm:grid-cols-3" data-testid="capacity-timing">
              {[["now", "Right now"], ["from_date", "From a date"], ["staggered", "Staggered dates"]].map(([value, label]) => (
                <label key={value} className={`flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm ${timing === value ? "border-primary bg-white ring-1 ring-primary/30" : "border-slate-200 bg-white"}`} data-testid={`capacity-timing-${value}`}>
                  <RadioGroupItem value={value} /> {label}
                </label>
              ))}
            </RadioGroup>
            {referralType === "other" && (
              <Input value={referralOther} onChange={(e) => setReferralOther(e.target.value)} placeholder="Describe the referral type you accept" className="mt-2" data-testid="capacity-psych-other-text" />
            )}
          </div>

          {timing !== "staggered" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="capacity-count">How many patients?</Label>
                <Input id="capacity-count" type="number" min="1" value={count} onChange={(e) => setCount(e.target.value)} placeholder="e.g. 2" data-testid="capacity-count-input" />
              </div>
              {timing === "from_date" && (
                <div className="space-y-1.5">
                  <Label htmlFor="capacity-from">Available from</Label>
                  <Input id="capacity-from" type="date" min={todayISO()} value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="capacity-from-input" />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Openings by date</Label>
              {stagger.map((row, index) => (
                <div key={index} className="flex items-end gap-2" data-testid={`capacity-stagger-row-${index}`}>
                  <div className="w-24 space-y-1">
                    <span className="text-xs text-slate-500">Patients</span>
                    <Input type="number" min="1" value={row.patient_count} onChange={(e) => updateStaggerRow(index, { patient_count: e.target.value })} data-testid={`capacity-stagger-count-${index}`} />
                  </div>
                  <div className="flex-1 space-y-1">
                    <span className="text-xs text-slate-500">From</span>
                    <Input type="date" min={todayISO()} value={row.available_from} onChange={(e) => updateStaggerRow(index, { available_from: e.target.value })} data-testid={`capacity-stagger-date-${index}`} />
                  </div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeStaggerRow(index)} disabled={stagger.length <= 1} data-testid={`capacity-stagger-remove-${index}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addStaggerRow} data-testid="capacity-stagger-add">
                <Plus className="mr-1.5 h-4 w-4" /> Add another date
              </Button>
            </div>
          )}

          {psych && (
            <div className="space-y-2">
              <Label>Referrals you accept</Label>
              <RadioGroup value={referralType} onValueChange={setReferralType} className="grid gap-2 sm:grid-cols-3" data-testid="capacity-psych-referral">
                {PSYCH_REFERRAL_OPTIONS.map((option) => (
                  <label key={option.value} className={`flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm ${referralType === option.value ? "border-primary bg-white ring-1 ring-primary/30" : "border-slate-200 bg-white"}`} data-testid={`capacity-psych-${option.value}`}>
                    <RadioGroupItem value={option.value} /> {option.label}
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            {hasSaved ? (
              <Button type="button" variant="ghost" size="sm" onClick={clear} disabled={clearing || saving || locked} data-testid="capacity-clear-button">
                {clearing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Remove precise numbers
              </Button>
            ) : <span />}
            <Button type="button" onClick={save} disabled={saving || locked} data-testid="capacity-save-button">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {expired ? "Reconfirm capacity" : "Confirm capacity"}
            </Button>
          </div>
          <p className="text-xs text-slate-500">Precise numbers stay visible to GPs for 7 days, then hide automatically until you reconfirm. Your general availability status always stays live.</p>
        </div>
      )}
    </div>
  );
};
